const ServiceDoc = () => {
  return (
    <section className="mb-12 text-slate-300">
      {/* Title */}
      <h2 className="text-3xl font-bold text-white mb-6">
        Service Layer — Usage Guide
      </h2>

      <p className="mb-6">
        The AxisNet Service Layer provides a collection of modular, reusable and
        infrastructure-focused services that aim to remove duplication and
        improve architectural clarity. Each service is designed to encapsulate a
        highly specific responsibility — such as retrieving geo-location from an
        IP address, hashing passwords, generating JWTs, sending emails,
        translating localized resources, validating Recaptcha tokens, caching
        values in Redis, or inspecting the client’s device environment. These
        services ensure that the application layer remains clean and focused
        solely on business logic while complex infrastructure concerns are
        abstracted behind simple, expressive interfaces.
      </p>

      {/* Service List */}
      <h3 className="text-2xl font-semibold text-white mb-4 mt-10">
        Available Core Services
      </h3>

      <ul className="space-y-3 bg-slate-900/50 border border-slate-800 p-6 rounded-lg">
        <li>
          <strong className="text-emerald-400">ClientInfoService</strong> —
          Extracts all relevant client metadata directly from the incoming HTTP
          request including IP address, operating system, browser family, device
          type and user-agent string. Internally relies on UAParser and
          IHttpContextAccessor.
        </li>

        <li>
          <strong className="text-emerald-400">GeoLocationService</strong> —
          Contacts external IP-lookup services (ipwho.is) to return detailed
          geographic information such as country, region, latitude, longitude,
          and ASN. Highly useful for logging, context-aware security, analytics,
          or fraud detection.
        </li>

        <li>
          <strong className="text-emerald-400">HashService</strong> — Offers
          secure password hashing and verification based on the
          industry-standard BCrypt algorithm. Ensures that sensitive credential
          information never gets stored in plain-text.
        </li>

        <li>
          <strong className="text-emerald-400">JwtService</strong> — Generates
          signed JWT tokens using claim dictionaries. Encapsulates issuer,
          audience, and secret-key configuration via
          <em>JwtOptions</em>, enabling uniform token generation across all
          authentication flows.
        </li>

        <li>
          <strong className="text-emerald-400">LocalizationService</strong> —
          Provides full multi-language support by loading internationalized
          resource files stored under <code>Resources/{"{lang}"}.json</code>.
          Fetches translated strings and supports interpolation with dynamic
          placeholders. Enables full i18n across backend logic, validation
          messages, exceptions, and service responses.
        </li>

        <li>
          <strong className="text-emerald-400">MailService</strong> — Sends raw
          or HTML-based emails using SMTP. Built around
          <em>MailOptions</em> configuration and supports dynamic template
          variable replacement, rich formatting and HTML layouts.
        </li>

        <li>
          <strong className="text-emerald-400">RecaptchaService</strong> —
          Validates Google Recaptcha v3/v2 tokens to ensure user input comes
          from real human interactions. Excellent for public forms, login
          endpoints and high-risk actions.
        </li>

        <li>
          <strong className="text-emerald-400">RedisCacheService</strong> — A
          general-purpose caching mechanism that stores structured or primitive
          values for fast retrieval. Enables application-level throttling, OTP
          expiration, session caching, and stateful behaviors.
        </li>

        <li>
          <strong className="text-emerald-400">VerificationCodeService</strong>{" "}
          — Generates numeric time-sensitive verification codes suitable for
          email/SMS confirmation flows, password resets, and identity
          verification steps.
        </li>
      </ul>

      {/* INDIVIDUAL SERVICE SECTIONS */}
      <h3 className="text-2xl font-semibold text-white mb-4 mt-10">
        Detailed Usage Examples
      </h3>

      <div className="space-y-10">
        {/* CLIENT INFO */}
        <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg">
          <h4 className="text-xl font-semibold text-emerald-400 mb-2">
            ClientInfoService — Full Client Environment Detection
          </h4>

          <p className="mb-3">
            <strong>ClientInfoService</strong> inspects the incoming HTTP
            request and extracts all relevant metadata, including:
            <br />• Public IP address
            <br />• Operating system
            <br />• Browser family and version
            <br />• Device type (desktop, mobile, tablet)
            <br />• Raw User-Agent string
            <br />
            <br />
            This data is often used for login auditing, suspicious-activity
            detection, analytics, device binding and security logs.
          </p>

          <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">
            {`var info = _clientInfoService.GetClientInfo();

Console.WriteLine(info.IpAddress);
Console.WriteLine(info.Os);
Console.WriteLine(info.Browser);
Console.WriteLine(info.Device);
Console.WriteLine(info.UserAgent);`}
          </pre>
        </div>

        {/* GEO LOCATION */}
        <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg">
          <h4 className="text-xl font-semibold text-emerald-400 mb-2">
            GeoLocationService — Resolve Country, Region & Coordinates
          </h4>

          <p className="mb-3">
            <strong>GeoLocationService</strong> communicates with
            <em>ipwho.is</em> to translate an IP address into meaningful
            geographic information. Useful for:
            <br />• Suspicious login monitoring
            <br />• Content/language optimization
            <br />• Regional analytics
            <br />• Fraud detection flows
          </p>

          <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">
            {`var geo = await _geoLocationService.GetLocationAsync("8.8.8.8");

Console.WriteLine(geo.Country);
Console.WriteLine(geo.Region);
Console.WriteLine(geo.Latitude);
Console.WriteLine(geo.Longitude);`}
          </pre>
        </div>

        {/* HASH SERVICE */}
        <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg">
          <h4 className="text-xl font-semibold text-emerald-400 mb-2">
            HashService — Secure Password Hashing with BCrypt
          </h4>

          <p className="mb-3">
            AxisNet’s <strong>HashService</strong> provides a robust wrapper
            around BCrypt for storing and verifying password hashes. This is
            essential for identity systems, as it prevents storing user
            credentials in plain-text and protects against rainbow-table
            attacks.
          </p>

          <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">
            {`string hash = _hashService.HashPassword("admin123");

bool valid = _hashService.VerifyPassword("admin123", hash);

if (!valid)
    throw new Exception("Password mismatch");`}
          </pre>
        </div>

        {/* JWT SERVICE */}
        <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg">
          <h4 className="text-xl font-semibold text-emerald-400 mb-2">
            JwtService — Generate HMAC-Signed JWT Tokens
          </h4>

          <p className="mb-3">
            <strong>JwtService</strong> uses <em>JwtOptions</em> to sign JSON
            Web Tokens using a symmetric HMAC SHA-256 key. Claims are passed in
            as a dictionary, making this service extremely flexible for user
            sessions, system tokens, service-level credentials, and
            machine-to-machine authentication.
          </p>

          <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">
            {`var token = _jwtService.GenerateToken(new Dictionary<string,string> {
    { "sub", user.Id.ToString() },
    { "email", user.Email },
    { "role", "Admin" }
});`}
          </pre>

          <div className="bg-slate-900/40 border border-slate-800 p-6 rounded-lg mt-4">
            <h5 className="text-lg font-semibold text-emerald-400 mb-2">
              JwtOptions — Required Configuration (appsettings.json)
            </h5>

            <p className="mb-3">
              <strong>JwtService</strong> uses <em>JwtOptions</em> to generate
              HMAC-SHA256 signed tokens. These values determine the issuer
              identity, who can consume the token, its lifetime, and the signing
              key used for validation.
            </p>

            <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">
              {`"JwtOptions": {
  "Issuer": "",            // Token issuer (ex: https://api.example.com)
  "Audience": "",          // Token consumer (frontend domain, mobile app, etc.)
  "SecretKey": "",         // MUST be >= 32 chars for secure HMAC
  "ExpiresInMinutes": 60   // Token validity period
}`}
            </pre>

            <p className="mt-3">
              Invalid or mismatched Issuer/Audience/SecretKey configurations
              will cause token validation failures across the API. Ensure they
              match your API gateway and authentication infrastructure.
            </p>
          </div>
        </div>

        {/* LOCALIZATION */}
        <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg">
          <h4 className="text-xl font-semibold text-emerald-400 mb-2">
            LocalizationService — Multi-Language Support (Resources/{"{lang}"}
            .json)
          </h4>

          <p className="mb-3">
            AxisNet’s <strong>LocalizationService</strong> dynamically loads all
            translations from resource files inside:
            <br />
            <code>Resources/en.json</code>,<code>Resources/tr.json</code>,
            <code>Resources/de.json</code>, etc.
            <br />
            <br />
            This allows:
            <br />• Centralized i18n management
            <br />• Localized API responses
            <br />• Multi-language validation messages
            <br />• Consistent messaging across the application
            <br />
            <br />
            Supports optional placeholder interpolation (ex: "{name} is
            required").
          </p>

          <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">
            {`string msg = _localizationService.Translate(
    "auth.invalid_credentials"
);`}
          </pre>
        </div>

        {/* MAIL SERVICE */}
        <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg">
          <h4 className="text-xl font-semibold text-emerald-400 mb-2">
            MailService — SMTP Email Delivery
          </h4>

          <p className="mb-3">
            <strong>MailService</strong> leverages configured SMTP credentials
            to send plain text or fully HTML-rendered email templates. Ideal for
            password recovery, user onboarding, system alerts, newsletters, and
            verification codes.
          </p>

          <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">
            {`await _mailService.SendAsync(
    "user@example.com",
    "Welcome!",
    "<h1>Hello!</h1><p>Your account is ready.</p>",
    true
);`}
          </pre>

          {/* MAIL SERVICE CONFIG */}
          <div className="bg-slate-900/40 border border-slate-800 p-6 rounded-lg mt-4">
            <h5 className="text-lg font-semibold text-emerald-400 mb-2">
              MailOptions — Required Configuration (appsettings.json)
            </h5>

            <p className="mb-3">
              <strong>MailService</strong> requires SMTP configuration under
              <code className="text-emerald-300"> MailOptions </code> in
              <code className="text-emerald-300"> appsettings.json </code>.
              AxisNet uses these credentials to create a secure SMTP session at
              runtime.
            </p>

            <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">
              {`"MailOptions": {
  "DisplayName": "",   // Sender display name shown in inbox
  "Port": 587,         // SMTP port (587 = TLS, 465 = SSL)
  "Encryption": "",    // SSL | TLS | STARTTLS
  "Password": "",      // SMTP password
  "Host": "",          // SMTP server (smtp.gmail.com, smtp.yandex.com, etc.)
  "Username": "",      // SMTP username
  "From": ""           // Sender email address
}`}
            </pre>

            <p className="mt-3">
              All values above are required for a healthy mail delivery
              pipeline. Incorrect or missing values will prevent the SMTP client
              from authenticating.
            </p>
          </div>
        </div>

        {/* RECAPTCHA */}
        <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg">
          <h4 className="text-xl font-semibold text-emerald-400 mb-2">
            RecaptchaService — Google Recaptcha Validation
          </h4>

          <p className="mb-3">
            Helps protect the system from automated requests, brute-force
            attacks, and bot-created accounts. Commonly used in login,
            registration, contact forms and high-security API endpoints.
          </p>

          <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">
            {`bool ok = await _recaptchaService.ValidateAsync(request.Token);

if (!ok)
    throw new Exception("Recaptcha validation failed");`}
          </pre>

          {/* RECAPTCHA CONFIG */}
          <div className="bg-slate-900/40 border border-slate-800 p-6 rounded-lg mt-4">
            <h5 className="text-lg font-semibold text-emerald-400 mb-2">
              RecaptchaOptions — Required Configuration (appsettings.json)
            </h5>

            <p className="mb-3">
              <strong>RecaptchaService</strong> validates user Recaptcha tokens
              by sending a server-side verification request to Google’s
              verification API. The following configuration is required inside
              <code className="text-emerald-300"> appsettings.json </code>.
            </p>

            <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">
              {`"RecaptchaOptions": {
  "SecretKey": "",   // Recaptcha secret key provided by Google
  "VerifyUrl": ""    // Default: https://www.google.com/recaptcha/api/siteverify
}`}
            </pre>

            <p className="mt-3">
              The <strong>SecretKey</strong> must never appear on the frontend.
              It is used exclusively by the backend to validate human
              interactions.
            </p>
          </div>
        </div>

        {/* REDIS SERVICE */}
        <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg">
          <h4 className="text-xl font-semibold text-emerald-400 mb-2">
            RedisCacheService — Distributed Caching & Temporary Storage
          </h4>

          <p className="mb-3">
            Provides high-performance caching and temporary state storage for
            rate-limit counters, OTP expiration, session data, short-lived user
            context, and more. Supports typed serialization and pattern removal.
          </p>

          <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">
            {`await _redis.SetAsync("otp:user123", "927361", TimeSpan.FromMinutes(5));

string code = await _redis.GetAsync<string>("otp:user123");

await _redis.RemoveAsync("otp:user123");`}
          </pre>
        </div>

        {/* VERIFICATION CODE SERVICE */}
        <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg">
          <h4 className="text-xl font-semibold text-emerald-400 mb-2">
            VerificationCodeService — Numeric OTP Generator
          </h4>

          <p className="mb-3">
            Generates short numeric codes typically used for two-factor
            authentication, email verification, password reset flows, and
            sensitive account actions.
          </p>

          <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">
            {`string otp = _verificationCodeService.GenerateCode(6);

Console.WriteLine(otp); // e.g. "483920"`}
          </pre>
        </div>
      </div>
    </section>
  );
};

export default ServiceDoc;
